import React from 'react';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { formatPhone } from '../../shared';
import {
  Avatar,
  Block,
  Callout,
  ListPage,
  Row,
  SectionHead,
  Segmented,
} from '../../ui/v2';

/**
 * Profile — the signed-in user's identity and appearance preference.
 *
 * Read-only like the legacy page: account details come from the session
 * (`useAuth`) and are managed by an Owner, so the page says so once instead
 * of rendering disabled inputs. Appearance saves immediately through
 * `ThemeProvider`, the same `.dark` class the frame's user menu toggles.
 */
export default function ProfilePage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const fullName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
    : '';
  const phone = formatPhone(user?.phone);
  const roleLabel = user?.is_platform_admin ? 'Platform admin' : user?.role;

  return (
    <>
      <PageMeta
        title="AskArthur | Your Profile"
        description="Your AskArthur account"
      />
      <ListPage title="Profile">
        <div className="max-w-[960px] px-4 pb-10 pt-5 lg:px-6">
          {!user ? (
            <Callout tone="danger" title="Your profile could not be loaded.">
              Sign out and back in to refresh your session.
            </Callout>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Avatar name={fullName || user.email} size="md" />
                <div className="min-w-0">
                  <h2 className="truncate text-v2-16 font-semibold text-v2-fg">
                    {fullName || user.email}
                  </h2>
                  <p className="truncate text-v2-12 text-v2-fg-3">
                    {[roleLabel, user.store_name].filter(Boolean).join(' · ') ||
                      user.email}
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <SectionHead
                  title="Personal information"
                  description="Your name, contact details and role at this store. Ask a store Owner to change them."
                />
                <dl className="mt-4 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                  <div>
                    <Row label="First name" empty={!user.first_name}>
                      {user.first_name || undefined}
                    </Row>
                    <Row label="Last name" empty={!user.last_name}>
                      {user.last_name || undefined}
                    </Row>
                    <Row label="Email" empty={!user.email}>
                      {user.email || undefined}
                    </Row>
                  </div>
                  <div>
                    <Row label="Phone" empty={!phone}>
                      {phone ? (
                        <span className="v2-num">{phone}</span>
                      ) : undefined}
                    </Row>
                    <Row label="Role" empty={!roleLabel}>
                      {roleLabel || undefined}
                    </Row>
                    <Row label="Store" empty={!user.store_name}>
                      {user.store_name || undefined}
                    </Row>
                  </div>
                </dl>
              </div>

              <Block
                title="Appearance"
                description="Light or dark theme for this browser. Saved as soon as you choose."
                className="mt-8"
              >
                <Segmented
                  aria-label="Appearance"
                  value={theme}
                  onChange={(next) => next !== theme && toggleTheme()}
                  items={[
                    { key: 'light', label: 'Light' },
                    { key: 'dark', label: 'Dark' },
                  ]}
                />
              </Block>
            </>
          )}
        </div>
      </ListPage>
    </>
  );
}
